import type { MapboxGeoJSONFeature } from 'mapbox-gl';
import type { Anchor } from 'react-map-gl';

/** An area as its tile feature carries it. */
export interface AreaFeature {
  id: string;
  name: string;
  category: string | null;
  status: string | null;
  shared: boolean;
  dataSource: string | null;
  createdBy: string | null;
  grossAcres: number | null;
}

/**
 * A tile property as text, or null when the feature doesn't carry it (the tiles
 * omit empty fields).
 * @param {unknown} value The property.
 * @return {string | null} The text.
 */
const textOrNull = (value: unknown): string | null => {
  if (typeof value === 'string') return value === '' ? null : value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
};

/**
 * The area's fields off its tile feature, so an area outside the loaded table page
 * still has its card. Vector tiles can hand booleans back as text.
 * @param {MapboxGeoJSONFeature} feature The area's fill feature.
 * @return {AreaFeature} The area.
 */
export function areaFrom(feature: MapboxGeoJSONFeature): AreaFeature {
  const p = (feature.properties ?? {}) as Record<string, unknown>;
  const acres = Number(p.gross_acres);
  return {
    id: textOrNull(p.id) ?? textOrNull(feature.id) ?? '',
    name: textOrNull(p.name) ?? '',
    category: textOrNull(p.category),
    status: textOrNull(p.status),
    shared: p.shared === true || p.shared === 'true' || p.shared === 1,
    dataSource: textOrNull(p.data_source),
    createdBy: textOrNull(p.created_by),
    grossAcres:
      Number.isFinite(acres) && p.gross_acres !== undefined ? acres : null,
  };
}

/**
 * Whether a value is a plain record (a loaded table row).
 * @param {unknown} value The value.
 * @return {boolean} True for a non-null object.
 */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * The area's loaded table row, when it is on the current page.
 * @param {string} id The area's id.
 * @param {readonly unknown[] | undefined} rows The loaded table rows.
 * @return {Record<string, unknown> | null} The row.
 */
const loadedRow = (
  id: string,
  rows: readonly unknown[] | undefined
): Record<string, unknown> | null => {
  const row = rows?.find(
    (candidate) => isRecord(candidate) && textOrNull(candidate.id) === id
  );
  return isRecord(row) ? row : null;
};

/**
 * The area's page: by business id when its table row is loaded (the URL the table
 * and the Areas page use), else by the id the tile carries.
 * @param {string} id The area's id.
 * @param {readonly unknown[] | undefined} rows The loaded table rows.
 * @return {string} The page's path.
 */
export function areaHref(
  id: string,
  rows: readonly unknown[] | undefined
): string {
  const businessId = textOrNull(loadedRow(id, rows)?.businessId);
  return `/area_geometries/${businessId ?? id}`;
}

/**
 * The area's gross acres as the table shows them: the loaded row's, else the tile's.
 * Tiles keep numbers at lower precision, so the two can differ in the last decimal.
 * @param {AreaFeature} area The area off its tile.
 * @param {readonly unknown[] | undefined} rows The loaded table rows.
 * @return {number | null} The acres.
 */
export function areaAcres(
  area: AreaFeature,
  rows: readonly unknown[] | undefined
): number | null {
  const acres = loadedRow(area.id, rows)?.grossAcres;
  return typeof acres === 'number' && Number.isFinite(acres)
    ? acres
    : area.grossAcres;
}

/** Where a pinned card opens, and how big it may grow, to stay inside the map. */
export interface CardPlacement {
  anchor: Anchor;
  maxWidth: number;
  tableHeight: number;
}

// The card at its widest; its name, fields and the Associated Links label take ~200px
// above the table, which gets the rest up to 300px (the type curve area popup's).
const CARD_WIDTH = 700;
const CARD_HEIGHT = 500;
const CARD_CHROME_HEIGHT = 200;
const MAX_TABLE_HEIGHT = 300;
const MIN_TABLE_HEIGHT = 80;
const EDGE_BUFFER = 10;

/**
 * Opens the card toward the side with room (right and down first, else whichever is
 * larger), as the type curve area popup does, and sizes it to the space it has.
 * @param {{ x: number; y: number }} point The click, in map pixels.
 * @param {{ width: number; height: number }} size The map's size.
 * @return {CardPlacement} The anchor, the card's width and its table's height.
 */
export function cardPlacement(
  point: { x: number; y: number },
  size: { width: number; height: number }
): CardPlacement {
  const right = size.width - point.x - EDGE_BUFFER;
  const left = point.x - EDGE_BUFFER;
  const below = size.height - point.y - EDGE_BUFFER;
  const above = point.y - EDGE_BUFFER;
  const opensRight = right >= CARD_WIDTH || right >= left;
  const opensDown = below >= CARD_HEIGHT || below >= above;
  const vertical = opensDown ? below : above;
  return {
    // The anchor is the card's corner at the click, opposite the way it opens.
    anchor: `${opensDown ? 'top' : 'bottom'}-${opensRight ? 'left' : 'right'}`,
    maxWidth: Math.min(CARD_WIDTH, Math.max(opensRight ? right : left, 0)),
    tableHeight: Math.min(
      MAX_TABLE_HEIGHT,
      Math.max(vertical - CARD_CHROME_HEIGHT, MIN_TABLE_HEIGHT)
    ),
  };
}
